import React, { useMemo } from 'react';

const TODAY = new Date().toISOString().slice(0,10);
function getWeekBounds() {
  const d = new Date();
  const day = d.getDay();
  const mon = new Date(d); mon.setDate(d.getDate() - ((day + 6) % 7));
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  const prevMon = new Date(mon); prevMon.setDate(mon.getDate() - 7);
  const prevSun = new Date(sun); prevSun.setDate(sun.getDate() - 7);
  return {
    thisStart: mon.toISOString().slice(0,10),
    thisEnd:   sun.toISOString().slice(0,10),
    prevStart: prevMon.toISOString().slice(0,10),
    prevEnd:   prevSun.toISOString().slice(0,10),
  };
}
function inRange(d, s, e) { return d >= s && d <= e; }
function fmt(iso) {
  if (!iso) return '';
  const [y,m,d] = iso.split('-');
  return `${['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][parseInt(m)-1]} ${parseInt(d)}`;
}

const CSS = `
.wr{max-width:640px;margin:0 auto;padding:var(--sp4) var(--sp4) var(--sp12);font-family:var(--sans);}
@media(max-width:768px){.wr{padding-bottom:calc(var(--bot-nav-h,56px)+var(--sp10)+var(--safe-bottom,0px));}}
.wr-hdr{margin-bottom:var(--sp5);}
.wr-week-label{font-family:var(--sans);font-size:11px;color:var(--t3);letter-spacing:.04em;margin-top:4px;}
.wr-section{margin-bottom:var(--sp5);}
.wr-section-lbl{font-family:var(--sans);font-size:11px;font-weight:400;color:var(--t3);letter-spacing:0.05em;text-transform:lowercase;margin-bottom:8px;}
.wr-card{background:var(--s1);border:0.5px solid var(--b1);border-radius:var(--r3);overflow:hidden;}
.wr-stat-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;}
.wr-stat{background:var(--s1);border:0.5px solid var(--b1);border-radius:var(--r3);padding:14px 16px;}
.wr-stat-n{font-family:var(--sans);font-size:22px;font-weight:300;color:var(--ink);line-height:1;letter-spacing:-0.3px;}
.wr-stat-l{font-family:var(--sans);font-size:10px;color:var(--t3);letter-spacing:.04em;margin-top:4px;}
.wr-stat-delta{font-family:var(--sans);font-size:10px;margin-top:2px;}
.wr-delta-pos{color:var(--accent);}
.wr-delta-neg{color:var(--t3);}
.wr-row{display:flex;align-items:center;justify-content:space-between;padding:10px 16px;border-bottom:0.5px solid var(--b1);}
.wr-row:last-child{border-bottom:none;}
.wr-row-title{font-family:var(--sans);font-size:13px;color:var(--ink);}
.wr-row-sub{font-family:var(--sans);font-size:11px;color:var(--t3);}
.wr-row-date{font-family:var(--sans);font-size:11px;color:var(--accent);flex-shrink:0;}
.wr-empty-row{padding:12px 16px;font-family:var(--sans);font-size:12px;color:var(--t4);}
.wr-workout-row{display:flex;align-items:center;gap:10px;padding:10px 16px;border-bottom:0.5px solid var(--b1);}
.wr-workout-row:last-child{border-bottom:none;}
.wr-workout-dot{width:6px;height:6px;border-radius:50%;background:var(--accent);flex-shrink:0;}
.wr-workout-title{font-family:var(--sans);font-size:13px;color:var(--ink);flex:1;}
.wr-workout-meta{font-family:var(--sans);font-size:11px;color:var(--t3);}
.wr-goal-row{display:flex;align-items:center;gap:10px;padding:10px 16px;border-bottom:0.5px solid var(--b1);}
.wr-goal-row:last-child{border-bottom:none;}
.wr-goal-title{font-family:var(--sans);font-size:13px;color:var(--ink);flex:1;}
.wr-prog-bar{width:60px;height:4px;background:var(--b2);border-radius:2px;overflow:hidden;}
.wr-prog-fill{height:100%;background:var(--accent);border-radius:2px;}
.wr-task-row{display:flex;align-items:center;gap:10px;padding:8px 16px;border-bottom:0.5px solid var(--b1);}
.wr-task-row:last-child{border-bottom:none;}
.wr-check{width:14px;height:14px;border-radius:3px;background:color-mix(in srgb,var(--accent) 12%,transparent);border:0.5px solid color-mix(in srgb,var(--accent) 35%,transparent);flex-shrink:0;}
.wr-task-title{font-family:var(--sans);font-size:12px;color:var(--ink);flex:1;}
.wr-task-due{font-family:var(--sans);font-size:10px;color:var(--t3);flex-shrink:0;}
.wr-insight{display:flex;gap:10px;padding:10px 16px;border-bottom:0.5px solid var(--b1);align-items:flex-start;}
.wr-insight:last-child{border-bottom:none;}
.wr-insight-dot{width:5px;height:5px;border-radius:50%;background:var(--accent);flex-shrink:0;margin-top:5px;}
.wr-insight-text{font-family:var(--sans);font-size:13px;color:var(--t2);line-height:1.55;flex:1;}
`;

export default function WeeklyPage({ st = {}, sigs = [], setTab, setSt, notify }) {
  // Safe destructure with defaults
  const tasks = st.tasks || [];
  const workouts = st.workouts || [];
  const events = st.events || [];
  const finance = st.finance || [];
  const goals = st.goals || [];
  const { thisStart, thisEnd, prevStart, prevEnd } = getWeekBounds();


  const thisWeekWorkouts = useMemo(() => workouts.filter(w => inRange(w.date, thisStart, thisEnd)), [workouts, thisStart, thisEnd]);
  const prevWeekWorkouts = useMemo(() => workouts.filter(w => inRange(w.date, prevStart, prevEnd)), [workouts, prevStart, prevEnd]);
  const completedTasks   = useMemo(() => tasks.filter(t => t.done && inRange(t.completedAt || t.dueDate || '', thisStart, thisEnd)), [tasks, thisStart, thisEnd]);
  const thisWeekEvents   = useMemo(() => events.filter(e => inRange(e.date, thisStart, thisEnd)), [events, thisStart, thisEnd]);
  const thisWeekSpend    = useMemo(() => finance.filter(f => inRange(f.date || '', thisStart, thisEnd)).reduce((s,f) => s + (f.amount||0), 0), [finance, thisStart, thisEnd]);

  const nextStart = new Date(thisEnd); nextStart.setDate(nextStart.getDate() + 1);
  const nextEnd   = new Date(nextStart); nextEnd.setDate(nextStart.getDate() + 6);
  const nextStartStr = nextStart.toISOString().slice(0,10);
  const nextEndStr   = nextEnd.toISOString().slice(0,10);

  const nextWeekEvents = useMemo(() => events.filter(e => inRange(e.date, nextStartStr, nextEndStr)).sort((a,b) => a.date.localeCompare(b.date)), [events, nextStartStr, nextEndStr]);
  const upcomingTasks  = useMemo(() => tasks.filter(t => !t.done && t.dueDate && inRange(t.dueDate, nextStartStr, nextEndStr)).sort((a,b) => a.dueDate.localeCompare(b.dueDate)), [tasks, nextStartStr, nextEndStr]);
  const activeGoals    = useMemo(() => goals.filter(g => !g.archived && !g.completed).sort((a,b) => { const da = a.targetDate || '9999'; const db = b.targetDate || '9999'; return da.localeCompare(db); }).slice(0,5), [goals]);

  const thisVolume = thisWeekWorkouts.reduce((s,w) => s + parseFloat(w.miles||w.distance||0), 0);
  const prevVolume = prevWeekWorkouts.reduce((s,w) => s + parseFloat(w.miles||w.distance||0), 0);
  const volDelta   = prevVolume > 0 ? Math.round((thisVolume - prevVolume) / prevVolume * 100) : null;

  // Cross-domain weekly insights using sigs
  const weekInsights = useMemo(() => {
    const out = [];
    // Workout vs average
    const last4 = workouts.filter(w => { const d = new Date(TODAY); d.setDate(d.getDate()-28); return w.date >= d.toISOString().slice(0,10); });
    const avgPerWeek = last4.length / 4;
    if (avgPerWeek >= 1 && thisWeekWorkouts.length > avgPerWeek * 1.4) {
      out.push({ msg: `strong week — ${thisWeekWorkouts.length} workouts vs your average of ${avgPerWeek.toFixed(1)}/week` });
    } else if (avgPerWeek >= 2 && thisWeekWorkouts.length === 0 && new Date().getDay() >= 3) {
      out.push({ msg: `no workouts yet this week — your average is ${avgPerWeek.toFixed(1)}/week` });
    }
    // Tasks done
    if (completedTasks.length >= 5) out.push({ msg: `${completedTasks.length} tasks completed — solid week` });
    // Spend context
    if (thisWeekSpend > 0) out.push({ msg: `$${thisWeekSpend.toFixed(0)} spent this week` });
    // From sigs
    const warns = (sigs || []).filter(s => s.lvl === 'warn').slice(0, 2);
    warns.forEach(s => out.push({ msg: s.msg }));
    return out.slice(0, 4);
  }, [thisWeekWorkouts, completedTasks, thisWeekSpend, sigs, workouts]);

  return (
    <>
      <style>{CSS}</style>
      <div className="wr">
        <div className="wr-hdr">
          <div style={{fontFamily:'var(--sans)',fontSize:28,fontWeight:300,color:'var(--ink)',lineHeight:1}}>
            week · <span style={{fontWeight:400,color:'var(--accent)'}}>review</span>
          </div>
          <div className="wr-week-label">{fmt(thisStart)} — {fmt(thisEnd)}</div>
        </div>

        {/* Stats */}
        <div className="wr-section">
          <div className="wr-section-lbl">this week</div>
          <div className="wr-stat-grid">
            <div className="wr-stat">
              <div className="wr-stat-n">{thisWeekWorkouts.length}</div>
              <div className="wr-stat-l">workouts</div>
              {volDelta !== null && (
                <div className={`wr-stat-delta ${volDelta >= 0 ? 'wr-delta-pos' : 'wr-delta-neg'}`}>
                  {volDelta >= 0 ? '+' : ''}{volDelta}% vs last wk
                </div>
              )}
            </div>
            <div className="wr-stat">
              <div className="wr-stat-n">{completedTasks.length}</div>
              <div className="wr-stat-l">tasks done</div>
            </div>
            <div className="wr-stat">
              <div className="wr-stat-n">{thisWeekEvents.length}</div>
              <div className="wr-stat-l">events</div>
            </div>
          </div>
        </div>

        {/* Cross-domain insights */}
        {weekInsights.length > 0 && (
          <div className="wr-section">
            <div className="wr-section-lbl">insights</div>
            <div className="wr-card">
              {weekInsights.map((ins, i) => (
                <div key={i} className="wr-insight">
                  <div className="wr-insight-dot" />
                  <div className="wr-insight-text">{ins.msg}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Workouts */}
        {thisWeekWorkouts.length > 0 && (
          <div className="wr-section">
            <div className="wr-section-lbl">workouts</div>
            <div className="wr-card">
              {thisWeekWorkouts.map((w,i) => (
                <div key={i} className="wr-workout-row">
                  <div className="wr-workout-dot"/>
                  <div className="wr-workout-title">{(w.type || 'workout').toLowerCase()}</div>
                  <div className="wr-workout-meta">
                    {w.miles ? `${w.miles}mi` : w.duration ? `${w.duration}min` : fmt(w.date)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Goals */}
        {activeGoals.length > 0 && (
          <div className="wr-section">
            <div className="wr-section-lbl">goals</div>
            <div className="wr-card">
              {activeGoals.map(g => (
                <div key={g.id} className="wr-goal-row" onClick={() => setTab && setTab('goals')} style={{cursor:'pointer'}}>
                  <div className="wr-goal-title">{g.title}</div>
                  <div>
                    <div className="wr-prog-bar">
                      <div className="wr-prog-fill" style={{width:`${g.progress||0}%`}}/>
                    </div>
                    <div style={{fontFamily:'var(--sans)',fontSize:10,color:'var(--t3)',textAlign:'right',marginTop:2}}>{g.progress||0}%</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Next week */}
        <div className="wr-section">
          <div className="wr-section-lbl">next week · {fmt(nextStartStr)} — {fmt(nextEndStr)}</div>
          <div className="wr-card">
            {nextWeekEvents.length === 0 && upcomingTasks.length === 0 ? (
              <div className="wr-empty-row">nothing scheduled yet</div>
            ) : (
              <>
                {nextWeekEvents.slice(0,5).map((e,i) => (
                  <div key={i} className="wr-row">
                    <div>
                      <div className="wr-row-title">{e.title?.toLowerCase()}</div>
                      {e.time && <div className="wr-row-sub">{e.time}</div>}
                    </div>
                    <div className="wr-row-date">{fmt(e.date)}</div>
                  </div>
                ))}
                {upcomingTasks.slice(0,5).map((t,i) => (
                  <div key={i} className="wr-task-row">
                    <div className="wr-check"/>
                    <div className="wr-task-title">{(t.title || t.text || '').toLowerCase()}</div>
                    <div className="wr-task-due">{fmt(t.dueDate)}</div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
